import { useMemo, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { JOURS_COURTS, grilleMois, iso } from './calendrier';

interface CalendrierGrilleProps {
  mois: Date;
  /** Contenu d'une case jour — le mode (cockpit gestion, mon calendrier, collègues) reste dans l'appelant. */
  renderJour: (jour: Date, dateStr: string, dansLeMois: boolean) => ReactNode;
  /**
   * « Mes demandes » en cartes : chaque case est un groupe daté (lisible par un lecteur d'écran), aujourd'hui est marqué, les jours
   * passés grisés, les jours du mois voisin gardent leur contenu, une semaine passée et vide se replie. Sans cette option, la grille
   * est exactement celle de la gestion et des collègues (cases = pastilles).
   */
  cartes?: {
    /** Aujourd'hui, 'AAAA-MM-JJ'. */
    aujourdhui: string;
    /** Rien à montrer ce jour-là : si toute la semaine est passée et vide, ses cases se replient. */
    vide: (dateStr: string) => boolean;
  };
}

/** « jeudi 22 octobre » (sans l'année), pour nommer une case. */
function jourLisible(jour: Date): string {
  return new Intl.DateTimeFormat('fr-CH', { weekday: 'long', day: 'numeric', month: 'long' }).format(jour);
}

// Coquille de grille mensuelle (5 colonnes, lun→ven — pas de week-end) — le contenu de chaque
// case est fourni par l'appelant : les 3 vues Calendrier du Tableau de bord
// (cockpit gestion, Mon calendrier, Demandes des collègues) affichent des
// informations très différentes dans une case, mais la même grille.
// Colonnes en minmax(0, 1fr) : un nom long ne peut pas élargir une colonne.
export function CalendrierGrille({ mois, renderJour, cartes }: CalendrierGrilleProps) {
  const jours = useMemo(() => grilleMois(mois), [mois]);
  // Semaines (5 jours) : une semaine entièrement passée et vide est repliée, pour que la semaine d'aujourd'hui remonte.
  const semainesRepliees = useMemo(() => {
    if (!cartes) return null;
    const out = new Set<number>();
    for (let i = 0; i < jours.length; i += 5) {
      const semaine = jours.slice(i, i + 5).map(iso);
      if (semaine.every((d) => d < cartes.aujourdhui && cartes.vide(d))) out.add(i / 5);
    }
    return out;
  }, [jours, cartes]);
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
        {JOURS_COURTS.map((j) => (
          <div key={j} className="text-xs text-upper text-muted" style={{ padding: 8, borderBottom: '1px solid var(--line)' }} aria-hidden={cartes ? true : undefined}>{j}</div>
        ))}
        {jours.map((jour, i) => {
          const dateStr = iso(jour);
          const dansLeMois = jour.getMonth() === mois.getMonth();
          if (cartes) {
            const auj = dateStr === cartes.aujourdhui;
            const classe = ['cal-jour', !dansLeMois && 'cal-jour--hors', dateStr < cartes.aujourdhui && 'cal-jour--passe', semainesRepliees?.has(Math.floor(i / 5)) && 'cal-jour--compact']
              .filter(Boolean).join(' ');
            return (
              <div key={dateStr} className={classe} role="group" aria-labelledby={`cal-jour-${dateStr}`} aria-current={auj ? 'date' : undefined}>
                <div className={`cal-num${auj ? ' cal-num--auj' : ''}`} aria-hidden="true">{jour.getDate()}</div>
                <span className="cal-sr" id={`cal-jour-${dateStr}`}>{jourLisible(jour).replace(/^./, (c) => c.toUpperCase())}{auj ? ", aujourd'hui" : ''}</span>
                {renderJour(jour, dateStr, dansLeMois)}
              </div>
            );
          }
          return (
            <div key={dateStr} style={{
              minHeight: 110, padding: 8, borderTop: '1px solid var(--line)', borderLeft: '1px solid var(--line)',
              opacity: dansLeMois ? 1 : 0.4, display: 'flex', flexDirection: 'column', gap: 4,
            }}>
              <div className="text-xs font-semi">{jour.getDate()}</div>
              {renderJour(jour, dateStr, dansLeMois)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Navigation du mois : « ‹ DÉCEMBRE 2026 › Aujourd'hui », placée JUSTE AU-DESSUS de la grille (le mois en est le
// titre) — on change de mois là où l'on regarde. Même interaction dans toutes les vues Calendrier.
export function NavigationMois({ mois, onChange, idMois }: { mois: Date; onChange: (mois: Date) => void; /** Identifiant du libellé du mois (la grille s'y réfère pour se nommer). */ idMois?: string }) {
  return (
    <div className="calendrier-nav">
      <button className="btn btn-icon" aria-label="Mois précédent" onClick={() => onChange(new Date(mois.getFullYear(), mois.getMonth() - 1, 1))}><ChevronLeft className="icon-sm" /></button>
      <span className="calendrier-mois" id={idMois} aria-live="polite">{new Intl.DateTimeFormat('fr-CH', { month: 'long', year: 'numeric' }).format(mois)}</span>
      <button className="btn btn-icon" aria-label="Mois suivant" onClick={() => onChange(new Date(mois.getFullYear(), mois.getMonth() + 1, 1))}><ChevronRight className="icon-sm" /></button>
      <button className="btn btn-ghost btn-sm" onClick={() => onChange(moisDebutAujourdhui())}>Aujourd'hui</button>
    </div>
  );
}

function moisDebutAujourdhui(): Date {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
